<script>
import throttle from 'lodash/throttle';
import { onDestroy } from 'svelte';
import { Delta } from '@typewriter/editor';
import { createEditor, getWordcount } from '../editor';
import { editor, sprint, storedSprints, Status, saved } from '../data/sprints';

export let sprintId;
let page;
let pageEditor;

$: ready = !!(sprintId && $storedSprints && page);
$: if (ready) init();

const scheduleSave = throttle(save, 1000);


function init() {
  pageEditor = createEditor(page, $sprint && $sprint.content || new Delta());
  pageEditor.sprintId = sprintId;
  if ($sprint && $sprint.selection) {
    Promise.resolve().then(() => pageEditor.setSelection($sprint.selection));
  }

  pageEditor.on('editor-change', onChange);
  pageEditor.view.updateBrowserSelection();
  editor.set(pageEditor);

  return () => {
    editor.set(null);
    pageEditor.off('editor-change', onChange);
  };
}

function onChange(event) {
  const editor = this;
  if (event.contents) {
    $saved = false;
    const totalWordcount = getWordcount(editor.contents);
    let wordcount = $sprint.wordcount || 0;
    if (getStatus($sprint) === Status.Started) {
      wordcount += totalWordcount - ($sprint.totalWordcount || 0);
    }

    $sprint = {
      ...$sprint,
      content: editor.contents,
      selection: editor.selection,
      totalWordcount,
      wordcount,
    };
    scheduleSave(editor);
  } else {
    $sprint = {
      ...$sprint,
      selection: editor.selection,
    };
    save(editor);
  }
}

async function save(editor) {
  if (!$sprint) return;
  await storedSprints.save($sprint);
  $saved = true;
}

onDestroy(() => {
  editor.set(null);
  pageEditor && pageEditor.off('editor-change', onChange);
});
</script>

<div bind:this={page} class="page" contenteditable="false" placeholder="Once upon a time…" spellcheck="false"></div>

<style>
.page {
  --padding: 24px;
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  max-width: 816px; /* 8.5 inches */
  padding: var(--padding); /* 1 inch, 1/4 inch minimum */
  background-color: var(--page-background);
  box-shadow: var(--page-shadow);
  font-family: var(--page-font);
  font-size: var(--page-font-size); /* 12pt */
  min-height: 528px; /* 1056px; /* 11 inches */
  border-radius: 2px;
  overflow-wrap: break-word;
  word-break: break-word;
  page-break-before: always;
  cursor: text;
  contain: layout;
}
.page:not([contenteditable]), .page[contenteditable="false"] {
  cursor: not-allowed;
  background: repeating-linear-gradient(-45deg, var(--white), var(--white) 100px, var(--warm-grey-050) 100px, var(--warm-grey-050) 200px);
}
.page:focus {
  outline: none;
}
.page :global(a) {
  overflow-wrap: break-word;
  word-wrap: break-word;
  -ms-word-break: break-all;
  word-break: break-all;
  word-break: break-word;
  -ms-hyphens: auto;
  -moz-hyphens: auto;
  -webkit-hyphens: auto;
  hyphens: auto;
  contain: strict;
}
.page :global(p:first-child) {
  text-indent: 0;
}
.page :global(p) {
  margin: 0;
  line-height: 2.4em;
  text-indent: 2em;
}
@media (min-width: 600px) {
  .page {
    --padding: 48px;
  }
}
@media (min-width: 840px) {
  .page {
    --padding: 96px;
  }
}
@media print {
  .page {
    position: relative;
    width: auto;
    height: auto;
    max-width: none;
    background-color: none;
    color: #000;
    box-shadow: none;
    min-height: 0;
    border-radius: 0;
    padding: 0 !important;
    text-align: justify;
  }
  @page {
    size: 8.5in 11in;
    margin: 1in;
  }
}
</style>
